import React, { useState, useEffect, useCallback } from "react";
import { btn } from "../../../shared/styles/buttonStyles";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import { migrationApplication, migrationListPath } from "../migrationRoutes";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { ACTIVITY_LOG_POLL_INTERVAL_MS } from "../../../shared/utils/activityFeed";
import { getAccessToken } from "../../../shared/auth/getAccessToken";
import { MigrationStatusIcon, isMigrationFinished, isMigrationRunning } from "../migrationStatus";

type MigrationTask = {
  id: string;
  name: string;
  status: number;
  timestamp: string;
};

type MigrationLogLine = {
  Timestamp: string;
  Level: string;
  Message: string;
};

const MigrationDetails = () => {
  const location = useLocation();
  const migrationId = new URLSearchParams(location.search).get("migrationId");
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState<MigrationTask[] | null>(null);
  const [createdAt, setCreatedAt] = useState<string>();
  const [logs, setLogs] = useState<MigrationLogLine[] | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");
  const apiUrl = process.env.REACT_APP_API_URL;

  // quiet = background poll: no toast, keep the last tasks on failure
  const fetchData = useCallback(async (quiet = false) => {
    if (!migrationId) {
      return;
    }

    try {
      const accessToken = await getAccessToken();
      const response = await axios.get(`${apiUrl}/migration/${migrationApplication}`, {
        params: { migrationId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setTasks(response.data.success ? (response.data.data?.tasks ?? []) : []);
      setCreatedAt(response.data.data?.createdAt);
    } catch (ex) {
      if (!quiet) {
        toast.error("Failed to load migration details.");
        setTasks((current) => current ?? []);
      }
    }
  }, [apiUrl, migrationId]);

  const fetchLogs = async (taskId: string) => {
    if (!migrationId) {
      return;
    }

    try {
      setLogsLoading(true);
      const accessToken = await getAccessToken();
      const response = await axios.get(`${apiUrl}/migration/logs/${migrationApplication}`, {
        params: { migrationId, taskId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // The API answers with an object instead of a list when the log file does not exist yet
      setLogs(Array.isArray(response.data) ? response.data : []);
    } catch (ex) {
      toast.error("Failed to load migration logs.");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (instance && accounts.length > 0) {
      void fetchData();
    }
  }, [instance, accounts, fetchData]);

  // Refresh the timeline while any task is still waiting or running
  const hasActiveTasks = (tasks ?? []).some((task) => isMigrationRunning(task.status, createdAt));

  useEffect(() => {
    if (!hasActiveTasks) {
      return;
    }

    const intervalId = window.setInterval(() => void fetchData(true), ACTIVITY_LOG_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [hasActiveTasks, fetchData]);

  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Shows migration task status and logs.">
          <h3 className="text-sm font-semibold text-slate-800">Migration details</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Task Timeline lists each migration task and its status.</li>
            <li>Click a completed or failed task to load its logs.</li>
            <li>Check the logs for errors before running the migration again.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {activeTab === "operations" && (
        <div className="mt-2" id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0}>
          {/* Fixed height on desktop so the log pane scrolls on its own */}
          <section className="migration-list workspace-card flex flex-col overflow-hidden border border-slate-200 bg-white md:h-[70vh] md:flex-row">
            <div className="tasks flex min-h-0 flex-col overflow-y-auto border-b border-slate-200 px-5 py-4 md:w-1/3 md:border-b-0 md:border-r">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold leading-5 text-slate-800">Task Timeline</h3>
                  <p className="mt-1 text-[11px] leading-4 text-slate-500">
                    Click a completed or failed task to see its logs.
                  </p>
                </div>
                <button type="button" className={btn.secondarySmall} onClick={() => navigate(migrationListPath)}>
                  List
                </button>
              </div>
              <hr className="my-3 border-slate-200" />
              {!migrationId ? (
                <p className="px-3 py-6 text-center text-xs text-slate-500">No migration selected.</p>
              ) : tasks === null ? (
                <p className="px-3 py-6 text-center text-xs text-slate-500">Loading migration details...</p>
              ) : tasks.length === 0 ? (
                <p className="px-3 py-6 text-center text-xs text-slate-500">No tasks found.</p>
              ) : (
                <ul className="divide-y divide-slate-200">
                  {tasks.map((task) => (
                    <MigrationStep key={task.id} task={task} onOpenLogs={() => void fetchLogs(task.id)} />
                  ))}
                </ul>
              )}
            </div>
            <div className="logs min-h-[16rem] min-w-0 flex-1 overflow-y-auto bg-slate-900 px-5 py-4 font-mono text-xs leading-5 text-slate-100 md:w-2/3">
              <h3 className="mb-2 font-sans text-sm font-bold leading-5 text-white">Execution Logs</h3>
              {logsLoading ? (
                <p className="font-sans text-slate-400">Loading logs...</p>
              ) : logs === null ? (
                <p className="font-sans text-slate-400">Select a completed or failed task to see its logs.</p>
              ) : logs.length === 0 ? (
                <p className="font-sans text-slate-400">No logs for this task.</p>
              ) : logs.map((log, index) => (
                <div key={`${index}-${log.Timestamp}`}>
                  <span className="mr-2">{">"}</span>
                  <span className="mr-2">
                    {`${formatAuditDate(log.Timestamp)} ${formatAuditTime24Hour(log.Timestamp)}`}
                  </span>
                  <span className="mr-2 text-blue-400">{log.Level}</span>
                  <span className="whitespace-pre-wrap break-all">{log.Message}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

// Only finished tasks have logs, so only they are buttons
const MigrationStep = ({ task, onOpenLogs }: { task: MigrationTask; onOpenLogs: () => void }) => {
  const content = (
    <span className="flex gap-3">
      <MigrationStatusIcon status={task.status} />
      <span className="min-w-0 text-start">
        <span className="block text-xs font-semibold text-slate-800">{task.name}</span>
        <span className="mt-0.5 block text-[11px] text-slate-500">
          {`${formatAuditDate(task.timestamp)} ${formatAuditTime24Hour(task.timestamp)}`}
        </span>
      </span>
    </span>
  );

  return (
    <li>
      {isMigrationFinished(task.status) ? (
        <button
          type="button"
          onClick={onOpenLogs}
          className="flex w-full items-center rounded-sm px-3 py-3 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500"
        >
          {content}
        </button>
      ) : (
        <div className="flex items-center px-3 py-3">{content}</div>
      )}
    </li>
  );
};

export default MigrationDetails;
